import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Elements a keyboard user can actually land on — used by both the initial-focus effect and the Tab-cycle trap below. Excludes disabled controls and `tabindex="-1"` targets (programmatically focusable only, not part of the tab order). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Every currently-mounted dialog's card element — lets a dialog opened FROM
 * WITHIN another one (AemetFetchDialog/ConfirmDeleteDialog inside
 * EditSubBasinDialog, for instance) tell the outer dialog's own
 * click-outside check "that click was still inside a dialog, don't close".
 * Needed only because of the `document.body` portal below: nested dialogs
 * used to be real DOM descendants of the outer card, so a plain
 * `card.contains(e.target)` already covered this; portaled, a nested
 * dialog's card is a DOM *sibling* of the outer one instead, so `contains()`
 * alone can't see it anymore — without this registry, clicking a button
 * inside a nested dialog (e.g. "Start download") registered as a click
 * *outside* the outer one and closed it first, taking the nested dialog
 * down with it before its own click handler ever ran.
 */
const openDialogCards = new Set<HTMLElement>();

/**
 * Generic modal dialog: floating card, draggable by its header, no
 * screen-covering backdrop — used by every edit form (sub-basin, zone,
 * outlet point, aquifer, AEMET fetch...) instead of replacing the list
 * item/chip in place. Dragging it aside is the whole point for something
 * like "Fetch from AEMET": the map underneath (with the sub-basin and its
 * candidate stations) needs to stay visible and clickable, not dimmed
 * behind a backdrop.
 *
 * Centered by CSS alone at first (`top/left: 50%` + a `translate(-50%,
 * -50%)` in styles.css) — no need to measure anything to center a card of
 * unknown size. Only switches to explicit pixel `left`/`top` (and cancels
 * that transform) once the user actually starts dragging, computed from
 * the card's real position at that moment (`getBoundingClientRect`), so
 * there's no jump when the drag begins.
 *
 * Without a backdrop element to catch it, "click outside to close" is
 * detected the same way the top bar's own popovers already do it
 * (useClickOutside.ts) — a document-level `mousedown` listener that closes
 * whenever the click didn't land inside the card. Since a drag also starts
 * with a `mousedown` on the header (inside the card), dragging never
 * triggers this. To avoid a dialog getting dragged somewhere unreachable
 * (the only way back would then be a click outside, or Escape), the drag
 * is clamped to always keep at least `EDGE_MARGIN_PX` of the card on
 * screen.
 *
 * Rendered into `document.body` via a portal, not in place: a caller like
 * SubBasinsPanel.tsx's own EditSubBasinDialog lives deep inside
 * `.edit-panel` (`position: absolute; z-index: 25`), which — like any
 * positioned element with its own z-index — establishes its own stacking
 * context. Rendered in place, `.dialog-card`'s z-index would only ever be
 * compared against `.edit-panel`'s OTHER children, never against siblings
 * of `.edit-panel` itself (e.g. `.ephemeral-banner`, z-index 1000): the
 * whole `.edit-panel` subtree paints as one unit at z-index 25, so no
 * z-index on the dialog could ever lift it above that banner — a real bug
 * found this way (AEMET's "start download" button, on a short window,
 * ended up both visually covered AND click-intercepted by the banner). A
 * portal sidesteps the whole class of "which ancestor traps my stacking
 * context" bugs for every one of this component's ~10 callers at once,
 * instead of relying on each caller individually avoiding nesting a dialog
 * under a positioned ancestor (already done once for SubBasinMap.tsx's own
 * dialogs, and re-broken here without anyone intending to).
 */
const EDGE_MARGIN_PX = 48;

export function Dialog({
  title,
  onClose,
  children,
  anchor = "center",
  size = "normal",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** "top-right": tucked under the floating Settings button instead of centered — for a dialog that can pop up over Landing.tsx's own centered card (UploadExistingProjectsDialog.tsx), where centering both would stack them nearly flush, making it hard to tell where one ends and the other begins. Still draggable afterwards like any other dialog. */
  anchor?: "center" | "top-right";
  /** "wide": for a dialog whose content needs more horizontal room than the default width comfortably fits — EditSubBasinDialog.tsx's Series tab is the first case (its 4 series tabs + the AEMET fetch button wrapped to a second row at the default width). Opt-in per dialog rather than widening every dialog (most, like ConfirmDeleteDialog, are better narrow). */
  size?: "normal" | "wide";
}) {
  const t = useT();
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Every mounted Dialog registers its own independent listener for the
      // same native keydown event, with no DOM nesting to rely on (see the
      // component's own header comment on the portal) — without this
      // check, one Escape/Tab press with a dialog open inside another acted
      // on BOTH at once. `openDialogCards` is a Set, so it iterates in
      // insertion (mount) order — the LAST one is the most recently
      // opened, i.e. the topmost dialog, the only one that should react.
      const card = cardRef.current;
      const topmost = Array.from(openDialogCards).at(-1);
      if (card !== topmost) return;
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !card) return;
      // Keeps Tab/Shift+Tab cycling within this dialog instead of escaping
      // into whatever's still mounted underneath (the map, the toolbar) —
      // real gap found by deep-review (2026-08-29): without a backdrop
      // element, nothing else stopped focus from leaving.
      const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !card.contains(active);
      const atEdge = e.shiftKey ? outside || active === first : outside || active === last;
      if (atEdge) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    openDialogCards.add(card);
    return () => {
      openDialogCards.delete(card);
    };
  }, []);

  // Focuses something useful inside the dialog the moment it opens (a real
  // input when there is one, the card itself otherwise — see its
  // `tabIndex={-1}` below), and gives focus back to whatever had it before
  // once the dialog closes — a keyboard/screen-reader user otherwise has no
  // indication the dialog opened at all, and loses their place in the page
  // underneath once it closes. Real gap found by deep-review (2026-08-29).
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const card = cardRef.current;
    // Prefers the BODY's own first focusable control over the header's ✕ —
    // that's a real, reachable stop for the Tab-cycle trap above, but not a
    // useful place to land on open (a keyboard user wants the dialog's
    // actual content, not immediately "close").
    const body = card?.querySelector(".dialog-body");
    const focusable =
      body?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      card?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? card)?.focus();
    return () => {
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      const card = cardRef.current;
      if (!card || !(e.target instanceof Node)) return;
      if (card.contains(e.target)) return;
      // Not truly "outside" if the click landed inside a DIFFERENT open
      // dialog — see openDialogCards' own comment above.
      for (const other of openDialogCards) {
        if (other !== card && other.contains(e.target)) return;
      }
      onClose();
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose]);

  function startDrag(e: ReactMouseEvent) {
    // Not the ✕ button, or any other control that might end up in the
    // header later — only an empty part of the header (or the title text)
    // starts a drag.
    if ((e.target as HTMLElement).closest("button")) return;
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };

    function onMouseMove(ev: MouseEvent) {
      const drag = dragRef.current;
      if (!drag || !card) return;
      const { width } = card.getBoundingClientRect();
      const left = clamp(
        drag.startLeft + (ev.clientX - drag.startX),
        EDGE_MARGIN_PX - width,
        window.innerWidth - EDGE_MARGIN_PX
      );
      const top = clamp(
        drag.startTop + (ev.clientY - drag.startY),
        0,
        window.innerHeight - EDGE_MARGIN_PX
      );
      setPosition({ left, top });
    }
    function onMouseUp() {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={[
        "dialog-card",
        anchor === "top-right" && "dialog-card--top-right",
        size === "wide" && "dialog-card--wide",
      ]
        .filter(Boolean)
        .join(" ")}
      style={position ? { left: position.left, top: position.top, transform: "none" } : undefined}
    >
      <div className="dialog-header" onMouseDown={startDrag}>
        <h3 id={titleId}>{title}</h3>
        <button className="button-icon" onClick={onClose} aria-label={t("common.close")}>
          ✕
        </button>
      </div>
      <div className="dialog-body">{children}</div>
    </div>,
    document.body
  );
}
